import $ from 'jquery'
import { AjaxHelper } from '~/common/extensions/ajax_helpers'
import { escapeHtml } from '~/common/helpers/escape_html'
import {
  setPendingSourceAttach,
  clearPendingSourceAttach,
} from './source_dnd.js'

// Rails UJS / AjaxHelper JSON responses (replace/update/remove/etc.)
export function sajaxJson(url, { method = 'GET', data, error } = {}) {
  return $.sajax({
    url,
    type: method,
    data,
    dataType: 'json',
    headers: { Accept: 'application/json' },
    error:
      error ||
      function (xhr, err, message) {
        AjaxHelper.errorFunc(xhr, err, message)
      },
  })
}

function csrfToken() {
  return document.querySelector('[name="csrf-token"]')?.content ?? ''
}

export async function postJson(url, formFields = {}) {
  const formData = new FormData()
  for (const [key, value] of Object.entries(formFields)) {
    if (value === null || value === undefined) continue
    formData.append(key, value)
  }

  const response = await fetch(url, {
    method: 'POST',
    body: formData,
    headers: {
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
      'X-CSRF-Token': csrfToken(),
    },
  })

  const json = await response.json().catch(() => ({}))
  if (!response.ok) {
    if (json.alert) AjaxHelper.doUpdate({ alert: json.alert })
    else console.error('Source drop action failed', response.status, json)
    return null
  }
  AjaxHelper.doUpdate(json)
  return json
}

export function attachSourceToComposer(payload, { force = true } = {}) {
  document.dispatchEvent(
    new CustomEvent('attachResource', {
      detail: {
        resource_id: payload.id,
        resource_type: 'ChatBot::Source',
        // Chip titles are pre-escaped HTML — escape the drag payload's plain title
        // at this boundary so it can't inject markup into the composer.
        title: escapeHtml(payload.title),
        type: payload.type,
        force,
      },
    })
  )
}

// Derive toggle URLs from source id — never trust URLs from the drag payload.
function sourceToggleUrl(sourceId, action) {
  return `/chat_bot/sources/${sourceId}/toggle_${action}`
}

// Force-star / force-archive: always send the explicit target state — the drag
// payload's starred/archived flags may be stale (rendered at page load), so a
// client-side skip could silently no-op and a blind toggle could UN-set the
// flag. The endpoint sets (not toggles) when given the param.
export function ensureSourceStarred(payload) {
  if (!payload.id) return
  return postJson(sourceToggleUrl(payload.id, 'starred'), { starred: 1 })
}

export function ensureSourceArchived(payload) {
  if (!payload.id) return
  return postJson(sourceToggleUrl(payload.id, 'archived'), { archived: 1 })
}

// Open a conversation and attach the source. ?source_id= seeds the server render;
// pending attach is scoped to conversationId and cleared if the open fails so a
// later composer mount cannot attach the source to the wrong conversation.
export function openConversationWithSource(
  conversationPath,
  payload,
  conversationId
) {
  const url = new URL(conversationPath, window.location.origin)
  url.searchParams.set('source_id', String(payload.id))
  const generation = setPendingSourceAttach(payload, conversationId)

  return sajaxJson(url.pathname + url.search, {
    method: 'GET',
    error(xhr, err, message) {
      clearPendingSourceAttach(generation)
      AjaxHelper.errorFunc(xhr, err, message)
    },
  })
}

export function startNewConversationWithSource(
  createNewConversationPath,
  sourceId
) {
  const url = new URL(createNewConversationPath, window.location.origin)
  url.searchParams.set('source_id', String(sourceId))
  // New conversation is created server-side with pending_attached_resources — no
  // client pending handoff needed (and no conversation id yet). Don't clear any
  // existing pending either: an in-flight drop on another conversation must keep
  // its own handoff; staleness is bounded by the pending's TTL and generation.
  sajaxJson(url.pathname + url.search, { method: 'GET' })
}

export function pinSourceToProject(projectId, sourceId) {
  return postJson(`/chat_bot/projects/${projectId}/sources`, {
    'add_source[source_id]': sourceId,
  })
}
